// /lib/chatUtils.ts
import { format, isToday, isYesterday, subDays, isAfter } from "date-fns";

export type Message = {
  from: string;
  body: string;
  type: "sent" | "received";
  sid?: string;
  messageSid?: string;
  dateSent?: string;
  isReply?: boolean;
  originalMessageSid?: string;
  replyToBody?: string;
  
  replyToMediaUrl?: string;
  replyToMediaType?: string;
  replyToFileName?: string;
  replyToFileSize?: number;
  
  // All communication_log table fields
  id?: number;
  sender: string;
  message: string;
  date_time: string;
  direct_response: boolean;
  context?: string;
  user_id: string;
  assistant_id?: string;
  media_url?: string;
  media_type?: string;
  file_name?: string;
  file_size?: number;
  
  // Computed/convenience fields
  imageUrl?: string;
  fileName?: string;
  fileSize?: number;
  messageType?: "text" | "image" | "audio" | "assistant";
};

export type ConversationSummary = {
  id: string;
  title: string;
  messages: Message[];
  date: string;
  time: string;
  status: "Resuelto" | "Urgente" | "Abierto";
  startTime?: string;
  endTime?: string;
  messageCount?: number;
  preview?: string;
};

// Helper function to filter out incomplete image messages and duplicates
function filterIncompleteImageMessages(messages: Message[]): Message[] {
  const filtered: Message[] = [];
  const processedMessages = new Map<string, Message>();

  messages.forEach(msg => {
    // Check if message is an image message
    const isImageMessage = msg.body?.includes('[Imagen recibida:') || msg.body?.includes('[Image received:');
    
    if (isImageMessage) {
      // Extract image filename from the message
      const filenameMatch = msg.body?.match(/\[Imagen recibida: (.+?)\]/) || msg.body?.match(/\[Image received: (.+?)\]/);
      const filename = filenameMatch ? filenameMatch[1] : null;
      
      if (filename) {
        // Create a unique key based on sender, filename and approximate time
        const timeKey = new Date(msg.date_time || msg.dateSent || '').getTime();
        const roundedTime = Math.floor(timeKey / 10000) * 10000; // Round to nearest 10 seconds
        const uniqueKey = `${msg.sender}_${filename}_${roundedTime}`;
        
        const existingMsg = processedMessages.get(uniqueKey);
        
        if (!existingMsg) {
          // First message with this key
          if (msg.media_url && msg.media_type && msg.file_name) {
            // Complete message - keep it
            processedMessages.set(uniqueKey, msg);
          } else {
            // Incomplete message - store temporarily but prefer complete ones
            processedMessages.set(uniqueKey, msg);
          }
        } else {
          // Duplicate found - keep the complete one
          const currentComplete = msg.media_url && msg.media_type && msg.file_name;
          const existingComplete = existingMsg.media_url && existingMsg.media_type && existingMsg.file_name;
          
          if (currentComplete && !existingComplete) {
            // Current is complete, existing is not - replace
            processedMessages.set(uniqueKey, msg);
          } else if (currentComplete && existingComplete) {
            // Both complete - keep the one with earlier timestamp
            const currentTime = new Date(msg.date_time || msg.dateSent || '').getTime();
            const existingTime = new Date(existingMsg.date_time || existingMsg.dateSent || '').getTime();
            if (currentTime < existingTime) {
              processedMessages.set(uniqueKey, msg);
            }
          }
          // If existing is complete and current is not, do nothing (keep existing)
        }
      }
    } else {
      // Non-image message - add directly
      filtered.push(msg);
    }
  });

  // Add all processed image messages
  processedMessages.forEach(msg => {
    // Only add image messages that have complete media information
    if (msg.media_url && msg.media_type && msg.file_name) {
      filtered.push(msg);
    }
  });

  // Sort by timestamp to maintain order
  return filtered.sort((a, b) => {
    const aTime = new Date(a.date_time || a.dateSent || '').getTime();
    const bTime = new Date(b.date_time || b.dateSent || '').getTime();
    return aTime - bTime;
  });
}

export function createConversationSummary(messages: Message[]): ConversationSummary {
  const filteredMessages = filterIncompleteImageMessages(messages);
  
  const enrichedMessages = filteredMessages.map(msg => {
    if (msg.isReply && msg.originalMessageSid && !msg.replyToMediaUrl) {
      const originalMsg = filteredMessages.find(
        m => (m.sid === msg.originalMessageSid || m.messageSid === msg.originalMessageSid)
      );
      if (originalMsg) {
        return {
          ...msg,
          replyToMediaUrl: msg.replyToMediaUrl || originalMsg.media_url || originalMsg.imageUrl,
          replyToMediaType: msg.replyToMediaType || originalMsg.media_type,
          replyToFileName: msg.replyToFileName || originalMsg.file_name || originalMsg.fileName,
          replyToFileSize: msg.replyToFileSize || originalMsg.file_size || originalMsg.fileSize,
        };
      }
    }
    return msg;
  });
  
  const first = enrichedMessages[0];
  const dateObj = new Date(first.dateSent!);
  
  // Crear un título más inteligente para imágenes
  let title = first.body?.slice(0, 30) || '';
  if (first.messageType === 'image') {
    title = first.fileName ? `📷 ${first.fileName.slice(0, 20)}` : '📷 Imagen';
  }
  
  return {
    id: `${first.dateSent}-${Math.random()}`,
    title,
    messages: enrichedMessages,
    date: format(dateObj, "dd/MM/yy"),
    time: format(dateObj, "hh:mmaaa"),
    status: "Abierto",
  };
}

export function groupMessagesIntoConversations(messages: Message[]): ConversationSummary[] {
  
  if (!messages || messages.length === 0) {
    return [];
  }

  // Filter out incomplete image messages first
  const filteredMessages = filterIncompleteImageMessages(messages);

  if (filteredMessages.length === 0) {
    return [];
  }
  
  const enrichedMessages = filteredMessages.map(msg => {
    if (msg.isReply && msg.originalMessageSid && !msg.replyToMediaUrl) {
      const originalMsg = filteredMessages.find(
        m => (m.sid === msg.originalMessageSid || m.messageSid === msg.originalMessageSid)
      );
      if (originalMsg) {
        return {
          ...msg,
          replyToMediaUrl: msg.replyToMediaUrl || originalMsg.media_url || originalMsg.imageUrl,
          replyToMediaType: msg.replyToMediaType || originalMsg.media_type,
          replyToFileName: msg.replyToFileName || originalMsg.file_name || originalMsg.fileName,
          replyToFileSize: msg.replyToFileSize || originalMsg.file_size || originalMsg.fileSize,
        };
      }
    }
    return msg;
  });

  const conversations: ConversationSummary[] = [];
  let currentConversation: Message[] = [];
  const GAP_THRESHOLD = 30 * 60 * 1000; 

  // Helper function para crear preview inteligente
  const createPreview = (msg: Message): string => {
    if (msg.messageType === 'image') {
      const imageText = msg.fileName ? `📷 ${msg.fileName}` : '📷 Imagen';
      const bodyText = msg.body?.trim() ? ` - ${msg.body}` : '';
      return `${imageText}${bodyText}`.substring(0, 50);
    }
    return msg.body?.substring(0, 50) + (msg.body?.length > 50 ? '...' : '');
  };

  // Helper function para crear título inteligente
  const createTitle = (msg: Message): any => {
    if (msg.messageType === 'image') {
      return msg.fileName ? `📷 ${msg.fileName.slice(0, 20)}` : '📷 Imagen';
    }
    return msg.body?.slice(0, 30) || '';
  };

  enrichedMessages.forEach((message, index) => {
    const messageTime = new Date(message.dateSent ?? "").getTime();
    
    if (currentConversation.length === 0) {
      currentConversation.push(message);
    } else {
      const lastMessageTime = new Date(currentConversation[currentConversation.length - 1].dateSent ?? "").getTime();
      const timeDiff = messageTime - lastMessageTime;
      
      if (timeDiff > GAP_THRESHOLD) {
        if (currentConversation.length > 0) {
          const firstMsg = currentConversation[0];
          const lastMsg = currentConversation[currentConversation.length - 1];
          
          conversations.push({
            id: `conv_${new Date(firstMsg.dateSent ?? "").getTime()}`,
            title: createTitle(firstMsg),
            messages: [...currentConversation],
            date: format(new Date(firstMsg.dateSent ?? ""), "dd/MM/yy"),
            time: format(new Date(firstMsg.dateSent ?? ""), "hh:mmaaa"),
            status: "Abierto",
            startTime: firstMsg.dateSent ?? "",
            endTime: lastMsg.dateSent ?? "",
            messageCount: currentConversation.length,
            preview: createPreview(firstMsg),
          });
        }
        
        currentConversation = [message];
      } else {
        currentConversation.push(message);
      }
    }
    
    if (index === filteredMessages.length - 1 && currentConversation.length > 0) {
      const firstMsg = currentConversation[0];
      const lastMsg = currentConversation[currentConversation.length - 1];
      
      conversations.push({
        id: `conv_${new Date(firstMsg.dateSent ?? "").getTime()}`,
        title: createTitle(firstMsg),
        messages: [...currentConversation],
        date: format(new Date(firstMsg.dateSent ?? ""), "dd/MM/yy"),
        time: format(new Date(firstMsg.dateSent ?? ""), "hh:mmaaa"),
        status: "Abierto",
        startTime: firstMsg.dateSent,
        endTime: lastMsg.dateSent,
        messageCount: currentConversation.length,
        preview: createPreview(firstMsg),
      });
    }
  });

  return conversations;
}

function createSummary(messages: Message[]): ConversationSummary {
  const first = messages[0];
  const dateObj = new Date(first.dateSent!);

  return {
    id: `${first.dateSent}-${Math.random()}`,
    title: first.body.slice(0, 30),
    messages,
    date: format(dateObj, "dd/MM/yy"),
    time: format(dateObj, "hh:mmaaa"),
    status: "Abierto", 
  };
}

export function groupConversationsByDate(
  conversations: ConversationSummary[]
) {
  const today: ConversationSummary[] = [];
  const yesterday: ConversationSummary[] = [];
  const last7: ConversationSummary[] = [];
  const last30: ConversationSummary[] = [];

  conversations.forEach((conv) => {
    const date = new Date(conv.messages[0].dateSent!);
    if (isToday(date)) today.push(conv);
    else if (isYesterday(date)) yesterday.push(conv);
    else if (isAfter(date, subDays(new Date(), 7))) last7.push(conv);
    else if (isAfter(date, subDays(new Date(), 30))) last30.push(conv);
  });

  return {
    Hoy: today,
    Ayer: yesterday,
    "Últimos 7 días": last7,
    "Últimos 30 días": last30,
  };
}

export function getAgentAbbreviation(agentName: string, numberOnly: boolean = false): string {
  if (!agentName) return "—";
  
  const agenteMatch = agentName.match(/^Agente\s+(\d+)$/i);
  if (agenteMatch) {
    return numberOnly ? agenteMatch[1] : `A${agenteMatch[1]}`;
  }
  
  const agentMatch = agentName.match(/^Agent\s+(\d+)$/i);
  if (agentMatch) {
    return numberOnly ? agentMatch[1] : `A${agentMatch[1]}`;
  }
  
  const words = agentName.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  
  return words
    .slice(0, 2)
    .map(w => w[0])
    .join("")
    .toUpperCase();
}
